import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AppIcon from '../AppIcon';

export interface CreatableSelectOption {
  value: number;
  label: string;
}

interface CreatableSelectProps {
  value?: number;
  options: CreatableSelectOption[];
  onChange: (value?: number) => void;
  // Omit to hide the create affordance. Returns the new id, or null if creation failed.
  onCreate?: (name: string) => Promise<number | null>;
  placeholder: string;
  disabled?: boolean;
  // Opt-in: renders an explicit "no selection" row so a clear is something the user ASKS for.
  // Only pass it where an empty value is a legitimate saved state (never on a required field).
  clearable?: boolean;
  clearLabel?: string;
  // Overrides the create row's wording where the generic "Add X" would not say what X becomes.
  // Interpolated with { name }; defaults to the shared phrasing.
  createLabelKey?: string;
  // Lets a visible <label htmlFor> name the field, which a placeholder alone does not do.
  id?: string;
  'data-testid'?: string;
}

const CreatableSelect: React.FC<CreatableSelectProps> = ({
  value,
  options,
  onChange,
  onCreate,
  placeholder,
  disabled = false,
  clearable = false,
  clearLabel,
  createLabelKey,
  id,
  'data-testid': testId,
}) => {
  const { t } = useTranslation();
  const selectedOption = options.find((o) => o.value === value);
  const selectedLabel = selectedOption?.label ?? '';
  const [query, setQuery] = useState(selectedLabel);
  const [isOpen, setIsOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  // True while the user is typing, so mirroring the selection never overwrites their input.
  const isEditingRef = useRef(false);
  const selectedLabelRef = useRef(selectedLabel);
  selectedLabelRef.current = selectedLabel;

  // CO-522: typing clears the parent's value (so a required field can't validate green against
  // stale text), which used to make an abandoned keystroke a SILENT clear — click outside and the
  // form saved NULL. The last pick the user actually committed is remembered here so we can put it
  // back. `value` alone can't do that: by then it is already undefined.
  const committedRef = useRef<CreatableSelectOption | null>(selectedOption ?? null);
  if (value != null) {
    // Options can arrive after the value (async fetch) or lag behind an inline create, so keep the
    // last label we managed to resolve rather than overwriting it with ''.
    committedRef.current = { value, label: selectedOption?.label ?? committedRef.current?.label ?? '' };
  } else if (!isEditingRef.current) {
    // value went null while the user was NOT typing => the PARENT cleared/reset it (e.g. the modal
    // reopened on a different record). Forget the pick, or a later abandoned keystroke would
    // restore the previous record's selection.
    committedRef.current = null;
  }

  // The click-outside listener is mounted once, so it must read these through refs — a closure
  // capture would freeze the first render's value/onChange.
  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // The selected option can arrive after the first render, so mirror it into the input.
  useEffect(() => {
    if (isEditingRef.current) return;
    setQuery(selectedLabel);
  }, [selectedLabel]);

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        // Text that never became a selection would otherwise linger and read as one.
        isEditingRef.current = false;
        const committed = committedRef.current;
        if (committed) {
          // Restoring only the LABEL would leave the parent holding undefined and still save NULL,
          // so re-commit the value too whenever typing has knocked it out.
          if (valueRef.current == null) onChangeRef.current(committed.value);
          setQuery(committed.label);
        } else {
          setQuery(selectedLabelRef.current);
        }
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const trimmed = query.trim();
  const matches = options.filter((o) => o.label.toLowerCase().includes(trimmed.toLowerCase()));
  const exactMatch = options.some((o) => o.label.toLowerCase() === trimmed.toLowerCase());
  const canCreate = !!onCreate && trimmed.length > 0 && !exactMatch;

  // clearLabel is required for the row to render — the component owns no wording of its own.
  const showClear = clearable && !!clearLabel && (value != null || committedRef.current != null);

  const handleSelect = (option: CreatableSelectOption) => {
    isEditingRef.current = false;
    committedRef.current = option;
    onChange(option.value);
    setQuery(option.label);
    setIsOpen(false);
  };

  // The one path that is allowed to end with no selection, because the user asked for it.
  const handleClear = () => {
    isEditingRef.current = false;
    committedRef.current = null;
    onChange(undefined);
    setQuery('');
    setIsOpen(false);
  };

  const handleCreate = async () => {
    if (!onCreate || creating) return;
    setCreating(true);
    setCreateError('');
    try {
      const newId = await onCreate(trimmed);
      if (newId == null) {
        setCreateError(t('creatableSelect.createFailed'));
        return;
      }
      // The parent may not have refetched its options yet, so keep the typed name showing — and
      // record it as the committed pick with its label, since `options` has no row for it yet.
      isEditingRef.current = false;
      committedRef.current = { value: newId, label: trimmed };
      onChange(newId);
      setQuery(trimmed);
      setIsOpen(false);
    } catch {
      setCreateError(t('creatableSelect.createFailed'));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <input
        id={id}
        type="text"
        value={query}
        disabled={disabled}
        onChange={(e) => {
          isEditingRef.current = true;
          setQuery(e.target.value);
          setIsOpen(true);
          setCreateError('');
          // Typing invalidates the current pick until something is chosen again.
          if (value != null) onChange(undefined);
        }}
        onFocus={() => !disabled && setIsOpen(true)}
        placeholder={placeholder}
        className="w-full border border-border rounded-lg px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary disabled:bg-muted disabled:text-muted-foreground"
        data-testid={testId}
      />

      {isOpen && (matches.length > 0 || canCreate || showClear) && (
        <div className="absolute z-50 mt-1 w-full overflow-y-auto rounded-lg border border-border bg-card shadow-lg max-h-60">
          {showClear && (
            <button
              type="button"
              onClick={handleClear}
              className="w-full border-b border-border px-3 py-2 text-start text-sm text-muted-foreground hover:bg-muted"
              data-testid={`${testId}-clear`}
            >
              {clearLabel}
            </button>
          )}

          {matches.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => handleSelect(o)}
              className="w-full px-3 py-2 text-start text-sm text-foreground hover:bg-muted"
              data-testid={`${testId}-option-${o.value}`}
            >
              {o.label}
            </button>
          ))}

          {canCreate && (
            <button
              type="button"
              onClick={handleCreate}
              disabled={creating}
              className="flex w-full items-center gap-2 border-t border-border px-3 py-2 text-start text-sm font-medium text-primary hover:bg-muted disabled:opacity-60"
              data-testid={`${testId}-create`}
            >
              {creating
                ? <AppIcon name="Loader2" size={14} className="animate-spin" aria-hidden="true" />
                : <AppIcon name="Plus" size={14} aria-hidden="true" />}
              {t(createLabelKey ?? 'creatableSelect.createOption', { name: trimmed })}
            </button>
          )}
        </div>
      )}

      {createError && <p className="mt-1 text-xs text-destructive">{createError}</p>}
    </div>
  );
};

export default CreatableSelect;
